import { Link } from '@/components/atoms';
import { cn } from '@/lib/utils';

export interface NavLink {
  to: string;
  label: string;
  icon?: React.ReactNode;
  active?: boolean;
}

export interface NavBarProps {
  links?: NavLink[];
  userControls?: React.ReactNode;
  logo?: React.ReactNode;
  className?: string;
}

/**
 * NavBar - Top navigation bar with dynamic links and user controls
 *
 * Usage:
 *   <NavBar
 *     logo={<Logo />}
 *     links={[{ to: '/', label: 'Home' }, { to: '/photos', label: 'Photos' }]}
 *     userControls={<UserControls user={user} onLogout={handleLogout} />}
 *   />
 */
export function NavBar({ logo, links = [], userControls, className }: NavBarProps) {
  return (
    <nav className={cn('flex items-center justify-between px-6 py-4 bg-surface-1 border-b border-border', className)} role="navigation" aria-label="Main navigation">
      {/* Logo/Branding */}
      <div className="flex items-center gap-8">
        {logo && <div className="flex-shrink-0 mb-1">{logo}</div>}

        {/* Navigation Links */}
        {links.length > 0 && (
          <div className="flex items-center gap-6">
            {links.map((link: NavLink) => (
              <Link
                key={`${link.label}-${link.to}`}
                to={link.to}
                variant="link-muted"
                className={cn(
                  'text-base transition-colors hover:text-primary flex items-center gap-2 whitespace-nowrap',
                  link.active
                    ? 'text-primary border-b-2 border-primary'
                    : 'text-foreground border-b-2 border-transparent'
                )}
                style={{ textDecoration: 'none' }}
              >
                {link.icon}
                {link.label}
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* User Controls (right side) */}
      {userControls && <div className="flex items-center">{userControls}</div>}
    </nav>
  );
}
